import { Entity, FloorFallView } from "game-common/models"
import { floorFallOffsetAt } from "game-common/floor_fall"

interface FallingSprite {
    y: number; alpha: number
    scale?: { x: number; y: number; set(x: number, y: number): void }
}
interface FallPlayback { view: FloorFallView; receivedAt: number; alpha: number; sx: number; sy: number; outgoing: boolean }
const ARRIVAL_HEIGHT_PX = 48
const playback = new WeakMap<FallingSprite, FallPlayback>()
interface ArrivalPlayback { id: string; startedAt?: number; durationMs: number; complete: boolean }
const arrivals = new WeakMap<FallingSprite, ArrivalPlayback>()
// The camera has the entity, not its sprite, and rides the same arrival the body plays.
const bodyArrivals = new WeakMap<Entity, ArrivalPlayback>()
const trajectories = new WeakMap<Entity, { view: FloorFallView; elapsedMs: number; receivedAt: number }>()
/**
 * A body this long in the air goes limp: it takes the downed pose mid-fall and lands in it. On the
 * server's gravity a drop lasts 240ms * sqrt(floors), so this is a fall through five storeys or more.
 */
export const FLOOR_FALL_DOWNED_AFTER_MS = 500
// The wire drops the fall for a message or two at every floor crossing (its view names the room
// below before the body has been moved there) and again just before "landed". A limp body held
// through that gap does not stand up and collapse again at each floor.
const KNOCKDOWN_GAP_MS = 200
// The fall that laid this sprite's body down, kept per sprite like arrivals. It hands over to
// "landed", which holds the same pose until the recovery ends.
const knockedDown = new WeakMap<FallingSprite, { id: string; seenAt: number }>()

/** Share one monotonic clock between the body and camera; delayed snapshots never rewind it. */
export const floorFallOffsetForRendering = (entity: Entity, now = Date.now()): number => {
    const view = entity.floorFall
    if (!view?.trajectory) { trajectories.delete(entity); return 0 }
    if (view.phase === "landed") return 0
    return floorFallOffsetAt(view, trajectoryElapsedMs(entity, view, now))
}

const trajectoryElapsedMs = (entity: Entity, view: FloorFallView, now: number): number => {
    let state = trajectories.get(entity)
    const wireElapsedMs = view.trajectory.endElapsedMs - view.remainingMs
    if (!state || state.view.id !== view.id) {
        state = { view, elapsedMs: wireElapsedMs, receivedAt: now }
        trajectories.set(entity, state)
    } else if (state.view !== view) {
        state.elapsedMs = Math.max(wireElapsedMs, state.elapsedMs + now - state.receivedAt)
        state.receivedAt = now
        state.view = view
    }
    return state.elapsedMs + now - state.receivedAt
}

/** Time spent in the air: stops where the body meets the ground it is falling towards (see floorFallOffsetAt). */
const airborneMs = (view: FloorFallView, elapsedMs: number): number => {
    const { gravity, endElapsedMs, totalDistance } = view.trajectory
    return Math.min(elapsedMs, view.phase === "falling" && totalDistance !== undefined
        ? Math.sqrt(2 * totalDistance / gravity) : endElapsedMs)
}

// LpcComposite lays a `downed` body down; the authoritative entity is never touched.
const limp = (view: Entity, sprite: FallingSprite): Entity =>
    knockedDown.has(sprite) ? Object.assign(Object.create(view), { downed: true }) : view

/** A room load may consume the wire arrival phase. Play it once after the curtain opens. */
export const floorFallForRendering = (entity: Entity, sprite: FallingSprite, visible = true, now = Date.now(), roomId = entity.roomId): Entity => {
    const view = entity.floorFall
    const knock = knockedDown.get(sprite)
    if (knock && (view ? view.id !== knock.id || view.phase === "landed" : now - knock.seenAt > KNOCKDOWN_GAP_MS)) {
        knockedDown.delete(sprite)
    } else if (knock && view) knock.seenAt = now
    // Messages and the active map can switch rooms on different frames. Do not
    // lift the body or start arrival playback while its destination is pending.
    if (view?.fromAbove && view.roomId && view.roomId !== roomId) {
        return limp(Object.assign(Object.create(entity), { floorFall: undefined }), sprite)
    }
    if (!view?.fromAbove) { arrivals.delete(sprite); return limp(entity, sprite) }
    // Chained floors use wire time, including late arrivals and intermediate rooms.
    // Replaying a local arrival would restart velocity on every room crossing.
    // Only these last long enough to go limp: a one-floor arrival is a fixed short drop.
    if (view.trajectory) {
        arrivals.delete(sprite)
        if (view.phase !== "landed" && !knockedDown.has(sprite) &&
            airborneMs(view, trajectoryElapsedMs(entity, view, now)) > FLOOR_FALL_DOWNED_AFTER_MS) {
            knockedDown.set(sprite, { id: view.id, seenAt: now })
        }
        return limp(entity, sprite)
    }
    let arrival = arrivals.get(sprite)
    if (!arrival || arrival.id !== view.id) {
        arrival = { id: view.id, durationMs: view.arrivalDurationMs ?? 300, complete: false }
        arrivals.set(sprite, arrival)
    }
    bodyArrivals.set(entity, arrival)
    if (arrival.complete) return landedArrival(entity)
    if (visible && arrival.startedAt === undefined) arrival.startedAt = now
    const durationMs = arrival.durationMs
    const elapsed = arrival.startedAt === undefined ? 0 : now - arrival.startedAt
    if (elapsed >= durationMs) { arrival.complete = true; return landedArrival(entity) }
    // Render a temporary view without changing the authoritative movement/recovery state.
    return Object.assign(Object.create(entity), { floorFall: { ...view,
        id: arrival.startedAt === undefined ? `${view.id}:waiting` : view.id, phase: "arriving",
        durationMs, remainingMs: durationMs - elapsed } })
}


/**
 * Local playback usually ends a frame or two before the server's "arriving" phase does. Handing the
 * wire view back then re-applied its stale remaining time and lifted the body again for a frame.
 */
const landedArrival = (entity: Entity): Entity => entity.floorFall?.phase === "arriving"
    ? Object.assign(Object.create(entity), { floorFall: undefined }) : entity

const arrivalLift = (progress: number): number => -ARRIVAL_HEIGHT_PX * (1 - progress * progress)

/**
 * The body's height for the camera to follow, as multi-floor falls already did. A one-floor drop
 * took 300ms while the floor-change pan took 600ms on its own curve, so the body sank below centre
 * and the camera kept drifting after it had landed.
 */
export const floorFallCameraOffset = (entity: Entity, roomId: string | undefined, now = Date.now()): number | undefined => {
    const view = entity.floorFall
    if (!view || view.phase === "landed" || (view.fromAbove && view.roomId && view.roomId !== roomId)) return undefined
    if (view.trajectory) return floorFallOffsetForRendering(entity, now)
    if (!view.fromAbove) return undefined
    const arrival = bodyArrivals.get(entity)
    if (arrival?.id !== view.id) return arrivalLift(0)
    if (arrival.complete) return undefined
    return arrivalLift(arrival.startedAt === undefined ? 0 : Math.min(1, (now - arrival.startedAt) / arrival.durationMs))
}

/** The departing body belongs below the active floor, above the lower companion room. */
export const floorFallZIndex = (entity: Entity, normal: number): number =>
    entity.floorFall?.phase === "falling" && !entity.floorFall.fromAbove ? -1 : normal


/** Run after normal placement. Remaining wire time is independent of client clock skew. */
export const applyFloorFall = (entity: Entity, sprite: FallingSprite, now = Date.now()): void => {
    let state = playback.get(sprite)
    if (state?.outgoing) { sprite.alpha = state.alpha; sprite.scale?.set(state.sx, state.sy) }
    const view = entity.floorFall
    if (!view || view.phase === "landed") { playback.delete(sprite); return }
    if (!state) {
        state = { view, receivedAt: now, alpha: sprite.alpha, sx: sprite.scale?.x ?? 1, sy: sprite.scale?.y ?? 1, outgoing: false }
        playback.set(sprite, state)
    } else if (state.view !== view) {
        state.view = view; state.receivedAt = now
    }
    const incoming = view.phase === "arriving" || view.fromAbove
    state.outgoing = !incoming
    if (view.trajectory) {
        sprite.y += floorFallOffsetForRendering(entity, now)
        return
    }
    const progress = Math.max(0, Math.min(1, 1 - (view.remainingMs - (now - state.receivedAt)) / view.durationMs))
    if (incoming) {
        sprite.y += arrivalLift(progress)
        return
    }
    sprite.y += view.distance * progress * progress
    sprite.alpha = state.alpha * (1 - .65 * progress)
    sprite.scale?.set(state.sx * (1 - .18 * progress), state.sy * (1 - .18 * progress))
}
